"use client";

import { useMemo, type ComponentType } from "react";
import { useLocale, useTranslations } from "next-intl";
import { clsx } from "clsx";
import type { Locale } from "@mw/shared/i18n";
import type { BillingPeriod } from "@mw/shared/pricing";
import { getPriceForCurrency } from "@mw/shared/pricing";
import { EventName } from "@mw/shared/analyticsEventNames";
import { useCurrency } from "@/app/_providers/currency";
import { formatPrice } from "@utilities/numbers";

import IconCircleCheck from "@ui/central-icons/icons/IconCircleCheck";
import IconCirclePlus from "@ui/central-icons/icons/IconCirclePlus";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import { TIER_COVER_IMAGES } from "../../utils";
import { MultilingualCover } from "../covers";
import type { CalculatorMode } from "../../PricingTableContext";
import { CalculatorResultCardShell } from "./CalculatorResultCardShell";
import {
  type PaygUsageInput,
  computePaygCostUsd,
  recommendSubscriptionTierMulti,
  tierPaygValueUnits,
} from "../payg-pricing-data";
import { formatUsage, getUnitShort, getUnitLong } from "./calculatorUtils";
import { appUrls } from "@/constants";

const BULLET_LIST_CLASS = "-tw-mx-2 tw-list-none tw-pl-0 tw-space-y-2 lg:tw-space-y-3";
const CARD_HEADING_CLASS = "tw-text-white tw-type-5xl";
const CARD_CAPTION_CLASS = "tw-text-white/60 tw-type-xs";

interface CardBulletProps {
  children: React.ReactNode;
  icon?: ComponentType<{ className?: string }>;
  tone?: "default" | "muted";
}

function CardBullet({ children, icon: Icon = IconCircleCheck, tone = "default" }: CardBulletProps) {
  return (
    <li
      className={clsx(
        "tw-flex tw-items-center tw-gap-2 tw-type-sm",
        tone === "muted" ? "tw-text-white/70 tw-tabular-nums" : "tw-text-white",
      )}
    >
      <Icon className={clsx("tw-size-5 tw-text-white/60 tw-flex-none", iconStrokeFix)} />
      <span>{children}</span>
    </li>
  );
}

// Slot reserved above the header so the layout doesn't shift when the badge appears
// or disappears as the recommendation crosses tier thresholds.
function MostPopularSlot({ label }: { label: string }) {
  return (
    <div className='-tw-mt-4 lg:-tw-mt-4 tw-mb-6 lg:tw-mb-8 tw-inline-flex tw-items-center tw-justify-center tw-border tw-border-white tw-text-white tw-px-3 tw-py-1 tw-rounded-lg tw-type-xs tw-font-medium tw-h-7 tw-w-fit'>
      {label}
    </div>
  );
}

interface CardHeaderProps {
  title: React.ReactNode;
  subtitle: string;
  price?: string;
  priceCaption?: string;
}

function CardHeader({ title, subtitle, price, priceCaption }: CardHeaderProps) {
  if (!price) {
    return (
      <div className='tw-mb-6 lg:tw-mb-8'>
        <h3 className={clsx(CARD_HEADING_CLASS, "tw-mb-2 tw-tabular-nums")}>{title}</h3>
        <p className={CARD_CAPTION_CLASS}>{subtitle}</p>
      </div>
    );
  }
  return (
    <div className='tw-grid tw-grid-cols-[1fr_auto] tw-items-baseline tw-gap-x-3 tw-gap-y-2 tw-mb-6 lg:tw-mb-8'>
      <h3 className={clsx(CARD_HEADING_CLASS, "tw-tabular-nums")}>{title}</h3>
      <p className={clsx(CARD_HEADING_CLASS, "tw-text-right tw-tabular-nums")}>{price}</p>
      <p className={CARD_CAPTION_CLASS}>{subtitle}</p>
      {priceCaption && <p className={clsx(CARD_CAPTION_CLASS, "tw-text-right")}>{priceCaption}</p>}
    </div>
  );
}

interface CardCtaFooterProps {
  href: string;
  label: string;
  eventProperties: Record<string, string>;
}

function CardCtaFooter({ href, label, eventProperties }: CardCtaFooterProps) {
  return (
    <div className='tw-mt-auto lg:-tw-mx-5 lg:-tw-mb-5 lg:tw-pt-6'>
      <LinkTracked
        eventName={EventName.mw_pricing_card_cta_clicked}
        eventProperties={eventProperties}
        asButton
        buttonVariants={{ variant: "elevated", size: "lg", expand: true }}
        className='!tw-rounded-2xl'
        href={href}
        label={label}
      />
    </div>
  );
}

export interface ApiCalculatorResultCardProps {
  usages: PaygUsageInput[];
  mode: CalculatorMode;
  period: BillingPeriod;
}

export function ApiCalculatorResultCard({ usages, mode, period }: ApiCalculatorResultCardProps) {
  const t = useTranslations("PricingClientKeys.PaygCostCalculator");
  const tCard = useTranslations("PricingClientKeys.PricingCard");
  const locale = useLocale() as Locale;
  const { currency, currencySymbol } = useCurrency();

  // Slider drags are hot. Skip the tier recommendation entirely in PAYG mode so we only sort/
  // filter tiers when the subscription card actually needs them. PAYG mode reuses the cheap
  // reduce that recommendSubscriptionTierMulti also runs internally.
  const recommendation = useMemo(
    () => (mode === "subscription" ? recommendSubscriptionTierMulti(usages, period) : null),
    [mode, usages, period],
  );

  const tier = recommendation?.tier;
  const monthlyPrice = recommendation?.monthlyPrice;
  const isEnterprise = recommendation?.isEnterprise ?? false;

  const tierName = tier ? tCard(`tiers.${tier.planId}.title` as Parameters<typeof tCard>[0]) : "";

  const formattedMonthlyPrice =
    monthlyPrice !== undefined
      ? formatPrice(getPriceForCurrency(monthlyPrice, currency), currencySymbol, locale)
      : null;
  const formattedTotalPaygCost =
    mode === "payg"
      ? formatPrice(
          getPriceForCurrency(
            usages.reduce((sum, { product, usage }) => sum + computePaygCostUsd(product, usage), 0),
            currency,
          ),
          currencySymbol,
          locale,
        )
      : null;

  const isMostPopular = tier?.planId === "creator";

  const subCtaLabel =
    isEnterprise || formattedMonthlyPrice === null
      ? t("subscription_enterprise_cta")
      : t("cta_label_start_tier_at_price", { tier: tierName, price: formattedMonthlyPrice });

  const coverImage =
    mode === "subscription" && tier ? (TIER_COVER_IMAGES[tier.planId] ?? TIER_COVER_IMAGES.payg) : MultilingualCover;

  return (
    <CalculatorResultCardShell bg='black' coverImage={coverImage}>
      {mode === "subscription" && tier ? (
        <>
          <CardHeader
            title={tierName}
            subtitle={t("subscription_card_subtitle")}
            price={formattedMonthlyPrice ?? undefined}
            priceCaption={
              formattedMonthlyPrice
                ? period === "yearly"
                  ? t("per_month_yearly_short")
                  : t("per_month_short")
                : undefined
            }
          />
          {isMostPopular && <MostPopularSlot label={t("badge_most_popular")} />}

          <ul className={clsx(BULLET_LIST_CLASS, "tw-mb-6 lg:tw-mb-0")}>
            {(() => {
              // Per-product "X included" bullets assume the entire tier budget is spent
              // on that product, which is only meaningful for a single-product basket —
              // summing them across a multi-product basket would overstate the allowance.
              const singleProduct = !isEnterprise && usages.length === 1 ? usages[0].product : null;
              const includedUnits = singleProduct ? tierPaygValueUnits(tier, singleProduct) : undefined;
              if (singleProduct && includedUnits !== undefined) {
                return (
                  <CardBullet>
                    {t("bullet_subscription_multi_includes", {
                      units: formatUsage(includedUnits, getUnitLong(t, singleProduct.unit)),
                      product: t(`products_short.${singleProduct.id}` as Parameters<typeof t>[0]),
                    })}
                  </CardBullet>
                );
              }
              return <CardBullet>{t("bullet_subscription_multi_covers")}</CardBullet>;
            })()}
            <CardBullet>{t("bullet_subscription_v3_cancel")}</CardBullet>
          </ul>

          <CardCtaFooter
            href={tier.cta.href}
            label={subCtaLabel}
            eventProperties={{ tier: tier.planId, source: "api_calculator_subscription" }}
          />
        </>
      ) : (
        <>
          <CardHeader title={formattedTotalPaygCost} subtitle={t("estimated_cost")} />

          {usages.length > 1 && (
            <ul className={clsx(BULLET_LIST_CLASS, "tw-mb-2 lg:tw-mb-3")}>
              {usages.map(({ product, usage }) => {
                const productName = t(`products_short.${product.id}` as Parameters<typeof t>[0]);
                const unitShort = getUnitShort(t, product.unit);
                const costUsd = computePaygCostUsd(product, usage);
                const cost = formatPrice(getPriceForCurrency(costUsd, currency), currencySymbol, locale);
                return (
                  <CardBullet key={product.id} icon={IconCirclePlus} tone='muted'>
                    {productName} · {formatUsage(usage, unitShort)} · <span className='tw-text-white'>{cost}</span>
                  </CardBullet>
                );
              })}
            </ul>
          )}

          <ul className={clsx(BULLET_LIST_CLASS, "tw-mb-6 lg:tw-mb-0")}>
            <CardBullet>{t("bullet_payg_v3_no_commitment")}</CardBullet>
            <CardBullet>{t("bullet_payg_v3_top_up")}</CardBullet>
          </ul>

          <CardCtaFooter
            href={appUrls.developers}
            label={t("cta_label")}
            eventProperties={{ tier: "payg", source: "api_calculator_payg" }}
          />
        </>
      )}
    </CalculatorResultCardShell>
  );
}
